import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  getCategoryColorClass,
  getCategoryLabelKey,
} from "../../../../models/legalDomainConstants";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Certification } from "../../../../models/certification";
import {
  getStatusLabelKey,
  IN_PROGRESS_STATUSES,
  TERMINAL_STATUSES,
} from "../../../../models/certificationStatus";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import { useAuth } from "../../../Reusable/AuthProvider";
import { getUserFullName, useDateFormatting } from "../../../Reusable/Utils";
import { getStatusVisual, StatusBadge } from "./certificationUi";

// Composant pour le texte expandable
interface ExpandableTextProps {
  text: string;
  maxLength?: number;
  className?: string;
}

function ExpandableText({
  text,
  maxLength = 100,
  className = "",
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  if (!text) return null;

  const shouldTruncate = text.length > maxLength;
  const displayText =
    shouldTruncate && !isExpanded
      ? text.substring(0, maxLength).trim() + "..."
      : text;

  return (
    <div className={className}>
      <p className="text-sm">{displayText}</p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary hover:text-primary/80 mt-2 font-medium"
        >
          {isExpanded
            ? t("CERTIFICATIONS_SHOW_LESS") || "Voir moins"
            : t("CERTIFICATIONS_SHOW_MORE") || "Voir plus"}
        </button>
      )}
    </div>
  );
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  CERTIFIED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  CERTIFIED_WITH_RESERVATIONS:
    "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  CORRECTED: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  DECLINED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  CANCELLED: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  PENDING: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300",
  IN_REVIEW:
    "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300",
  WAITING_FOR_USER:
    "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
};

const statusBadgeClass = (status: string) =>
  STATUS_BADGE_CLASS[status] || "bg-slate-100 text-slate-800";

export default function ExpertCertificationPage() {
  const getCertifications = (requestResponse: string) => {
    AxiosHttpClient.get<Certification[]>(
      "api/v1/docuveritas/conversations/messages/certifications/" +
        requestResponse
    )
      .then((res) => {
        setPendingCertifications(
          res.filter((c) => IN_PROGRESS_STATUSES.includes(c.status))
        );
        setCertifiedResponses(
          res.filter((c) => TERMINAL_STATUSES.includes(c.status))
        );
      })
      .catch((err) => {});
  };

  const { user } = useAuth();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormatting();

  const [activeTab, setActiveTab] = useState("pending");
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingCertifications, setPendingCertifications] = useState<
    Certification[]
  >([]);
  const [certifiedResponses, setCertifiedResponses] = useState<Certification[]>(
    []
  );

  const navigate = useNavigate();

  const handleCertifyClick = (certification) => {
    setSelectedCertification(certification);
    setShowModal(true);
  };

  const handleViewDetails = (certification) => {
    setSelectedCertification(certification);
    setShowDetailView(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleBackToList = () => {
    setShowDetailView(false);
    setSelectedCertification(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!user) return;
    if (user.roles.includes("EXPERT")) {
      getCertifications("response");
    } else if (user.roles.includes("USER")) {
      getCertifications("request");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goConsult = (certification: Certification, isClosed: boolean) => {
    if (!isClosed && user?.roles.includes("EXPERT")) {
      navigate("/feature/certifications/respond/" + certification.id);
    } else {
      navigate("/feature/certifications/details/" + certification.id);
    }
  };

  const renderEmptyState = (
    icon: React.ReactNode,
    title: string,
    description: string
  ) => (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {icon}
        </div>
        <h3 className="mb-1 text-lg font-semibold">{title}</h3>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );

  const renderCertificationCard = (
    certification: Certification,
    isClosed: boolean
  ) => {
    const visual = getStatusVisual(certification.status);
    const Icon = visual.icon;
    const expert = certification.certifiedBy;
    return (
      <Card
        key={certification.id}
        className={cn(
          "overflow-hidden border-l-4 transition-all hover:shadow-md",
          visual.accentBorder
        )}
      >
        <CardContent className="space-y-4 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                {formatDateTime(certification.requestedAt)}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  "border-transparent font-medium",
                  getCategoryColorClass(certification.category)
                )}
              >
                {t(getCategoryLabelKey(certification.category))}
              </Badge>
            </div>
            <StatusBadge status={certification.status} />
          </div>

          <h3 className="text-base font-semibold leading-snug">
            {certification.chatMessage.question?.content}
          </h3>

          <div className="rounded-lg border bg-muted/40 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              {t("CERTIFICATIONS_AI_RESPONSE") || "Réponse de l'IA"}
            </div>
            <ExpandableText
              text={certification.chatMessage.content || ""}
              maxLength={180}
              className="text-sm text-muted-foreground"
            />
          </div>

          {isClosed && expert && certification.finalDecision && (
            <div className={cn("rounded-lg border p-3", visual.softBg)}>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold">
                <Icon className="h-3.5 w-3.5" />
                {t(getStatusLabelKey(certification.status))}
              </div>
              <ExpandableText
                text={certification.finalDecision}
                maxLength={160}
                className="text-sm"
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={expert?.imageUrl || "/placeholder.svg"}
                  alt={getUserFullName(expert)}
                />
                <AvatarFallback>
                  <UserIcon className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 text-sm leading-tight">
                {expert ? (
                  <>
                    <p className="truncate font-medium">
                      {getUserFullName(expert)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isClosed && certification.certifiedAt
                        ? formatDateTime(certification.certifiedAt)
                        : t("CERTIFICATIONS_ASSIGNED_EXPERT") ||
                          "Expert assigné"}
                    </p>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    {t("CERTIFICATIONS_NO_EXPERT_ASSIGNED") ||
                      "Aucun expert assigné pour le moment"}
                  </span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => goConsult(certification, isClosed)}
            >
              {t("CERTIFICATIONS_CONSULT") || "Consulter"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  /*if (showDetailView && selectedCertification) {
    return (
      <CertificationDetails
        certification={selectedCertification}
        onBack={handleBackToList}
      />
    );
  }*/

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("CERTIFICATIONS_PAGE_TITLE") || "Certifications"}
          </h1>
          <p className="mt-1 max-w-2xl text-pretty text-muted-foreground">
            {t("CERTIFICATIONS_PAGE_DESCRIPTION") ||
              "Suivez le statut de vos demandes de certification et consultez les réponses validées par nos experts juridiques."}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mx-auto grid w-fit grid-cols-2">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t("CERTIFICATIONS_IN_PROGRESS_TAB_TITLE") || "En cours"} (
            {pendingCertifications.length})
          </TabsTrigger>
          <TabsTrigger value="certified" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {t("CERTIFICATIONS_CLOSED_TAB_TITLE") || "Clôturées"} (
            {certifiedResponses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="space-y-4">
            {pendingCertifications.length === 0
              ? renderEmptyState(
                  <Clock className="h-7 w-7" />,
                  t("CERTIFICATIONS_NO_PENDING") ||
                    "Aucune certification en attente",
                  t("CERTIFICATIONS_NO_PENDING_DESC") ||
                    "Vous n'avez actuellement aucune demande de certification en cours."
                )
              : pendingCertifications.map((certification) =>
                  renderCertificationCard(certification, false)
                )}
          </div>
        </TabsContent>

        <TabsContent value="certified" className="mt-6">
          <div className="space-y-4">
            {certifiedResponses.length === 0
              ? renderEmptyState(
                  <CheckCircle2 className="h-7 w-7" />,
                  t("CERTIFICATIONS_NO_CERTIFIED") ||
                    "Aucune certification validée",
                  t("CERTIFICATIONS_NO_CERTIFIED_DESC") ||
                    "Vous n'avez encore aucune réponse certifiée par nos experts."
                )
              : certifiedResponses.map((certification) =>
                  renderCertificationCard(certification, true)
                )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
